import { useRef, useMemo, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, Checkbox, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  IconPlaylist,
  IconChevronUp,
  IconChevronDown,
  IconEdit,
  IconCode,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
} from '@tabler/icons-react';
import { PlaylistGridRow } from './PlaylistGridRow';
import { useSelection } from '@/shared/contexts/SelectionContext';
import {
  PLAYLIST_ROW_HEIGHT,
  PLAYLIST_VIRTUALIZER_OVERSCAN,
  PLAYLIST_GRID_COLUMNS,
  PLAYLIST_GRID_MAX_VIRTUAL_ROWS,
  PLAYLIST_GRID_MAX_VIEWPORT_HEIGHT_PX,
} from '@/lib/constants/playlist';
import type { PlaylistGridRow as PlaylistGridRowType, PlaylistSortKey } from '@/lib/types/Playlist';
import type { SortDirection } from '@/lib/types/common';
import { recordSample } from '@/shared/utils/perf';
import styles from './PlaylistGrid.module.css';

interface PlaylistGridProps {
  count: number;
  allIds: number[];
  getRow: (index: number) => PlaylistGridRowType | null;
  sortKey: PlaylistSortKey;
  sortDirection: SortDirection;
  onSort: (key: PlaylistSortKey) => void;
  onPublish?: (playlist: PlaylistGridRowType) => void;
  onActivateToggle?: (playlist: PlaylistGridRowType) => void;
  onDelete?: (playlist: PlaylistGridRowType) => void;
  initialScrollTop?: number;
  onScrollTopChange?: (scrollTop: number) => void;
}

export function PlaylistGrid({
  count,
  allIds,
  getRow,
  sortKey,
  sortDirection,
  onSort,
  onPublish,
  onActivateToggle,
  onDelete,
  initialScrollTop = 0,
  onScrollTopChange,
}: PlaylistGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const didRestoreScrollRef = useRef(false);
  const didReportVirtualRowsViolationRef = useRef(false);
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return 720;
    }

    const estimated = Math.max(0, window.innerHeight - 220);
    return Math.min(estimated, PLAYLIST_GRID_MAX_VIEWPORT_HEIGHT_PX);
  });

  const navigate = useNavigate();
  const { selectedIds, isSelected, selectOnly, toggle, toggleRange, addToSelection, removeFromSelection } = useSelection();

  const allIdsRef = useRef<number[]>([]);
  allIdsRef.current = allIds;

  const virtualizer = useVirtualizer({
    count,
    // Seed the virtualizer's internal offset with the restored scroll position.
    // Its attach step syncs the element to that internal offset, so without this
    // a re-attach (e.g. StrictMode's dev double-mount) scrolls back to 0 and
    // undoes the scroll restore below.
    initialOffset: initialScrollTop,
    getScrollElement: () => parentRef.current,
    estimateSize: () => PLAYLIST_ROW_HEIGHT,
    overscan: PLAYLIST_VIRTUALIZER_OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const renderedVirtualRows = virtualRows.length > PLAYLIST_GRID_MAX_VIRTUAL_ROWS
    ? virtualRows.slice(0, PLAYLIST_GRID_MAX_VIRTUAL_ROWS)
    : virtualRows;

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const headerEl = headerRef.current;
    if (!containerEl || !headerEl || typeof ResizeObserver === 'undefined') return;

    const updateViewportHeight = () => {
      const measured = Math.max(0, containerEl.clientHeight - headerEl.offsetHeight);
      const fallback = typeof window !== 'undefined'
        ? Math.max(0, window.innerHeight - headerEl.offsetHeight - 140)
        : measured;
      const bounded = Math.min(
        measured > 0 ? measured : fallback,
        PLAYLIST_GRID_MAX_VIEWPORT_HEIGHT_PX
      );

      setViewportHeight((prev) => (Math.abs(prev - bounded) < 1 ? prev : bounded));
    };

    updateViewportHeight();

    const observer = new ResizeObserver(() => updateViewportHeight());
    observer.observe(containerEl);
    observer.observe(headerEl);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || didRestoreScrollRef.current) return;

    if (initialScrollTop > 0) {
      scrollEl.scrollTop = initialScrollTop;
      virtualizer.scrollToOffset(initialScrollTop);
    }

    didRestoreScrollRef.current = true;
  }, [initialScrollTop, virtualizer]);

  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !onScrollTopChange) return;

    const handleScroll = () => onScrollTopChange(scrollEl.scrollTop);
    handleScroll();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [onScrollTopChange]);

  useEffect(() => {
    if (virtualRows.length <= PLAYLIST_GRID_MAX_VIRTUAL_ROWS) {
      didReportVirtualRowsViolationRef.current = false;
      return;
    }

    if (didReportVirtualRowsViolationRef.current) return;
    didReportVirtualRowsViolationRef.current = true;

    recordSample('playlists.virtual.rows.count', virtualRows.length, {
      count,
      limit: PLAYLIST_GRID_MAX_VIRTUAL_ROWS,
      violation: true,
      viewportHeight,
    });

    console.error('[PlaylistGrid] Virtualization containment invariant violated', {
      virtualRows: virtualRows.length,
      limit: PLAYLIST_GRID_MAX_VIRTUAL_ROWS,
      viewportHeight,
      totalRows: count,
    });
  }, [virtualRows.length, viewportHeight, count]);

  const handleRangeSelect = useCallback(
    (id: number, index: number) => toggleRange(id, index, allIdsRef.current),
    [toggleRange]
  );

  const handleNavigate = useCallback((href: string) => {
    navigate({ to: href });
  }, [navigate]);

  const [menuState, setMenuState] = useState<{
    opened: boolean;
    rowIndex: number | null;
  }>({ opened: false, rowIndex: null });

  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = useCallback((index: number, buttonEl: HTMLElement) => {
    const rect = buttonEl.getBoundingClientRect();
    if (anchorRef.current) {
      anchorRef.current.style.position = 'fixed';
      anchorRef.current.style.top = `${rect.top}px`;
      anchorRef.current.style.left = `${rect.left}px`;
      anchorRef.current.style.width = `${rect.width}px`;
      anchorRef.current.style.height = `${rect.height}px`;
    }

    setMenuState({ opened: true, rowIndex: index });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuState((prev) => ({ ...prev, opened: false }));
  }, []);

  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !menuState.opened) return;

    const onScroll = () => handleMenuClose();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });

    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [menuState.opened, handleMenuClose]);

  const activePlaylist = menuState.rowIndex !== null ? getRow(menuState.rowIndex) : null;

  const handleRowDelete = useCallback((playlist: PlaylistGridRowType) => {
    modals.openConfirmModal({
      title: 'Delete Playlist',
      children: (
        <Text size="sm">
          Are you sure you want to delete &ldquo;{playlist.title}&rdquo;? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete?.(playlist),
    });
  }, [onDelete]);

  // Header checkbox scoped to the VISIBLE (filtered) rows — select-all under a
  // search must not drop selections made under a previous search. Memoized so
  // scroll re-renders don't pay O(n).
  const visibleSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    let n = 0;
    for (const id of allIds) {
      if (selectedIds.has(id)) n++;
    }
    return n;
  }, [allIds, selectedIds]);

  const allSelected = count > 0 && visibleSelectedCount === count;
  const someSelected = visibleSelectedCount > 0 && visibleSelectedCount < count;

  const handleHeaderCheckbox = () => {
    if (allSelected) {
      removeFromSelection(allIdsRef.current);
    } else {
      addToSelection(allIdsRef.current);
    }
  };

  const handleHeaderClick = (column: typeof PLAYLIST_GRID_COLUMNS[number]) => {
    if (column.sortable && column.sortKey) {
      onSort(column.sortKey);
    }
  };

  if (count === 0) {
    return (
      <Box ref={containerRef} className={styles.gridContainer}>
        <Box className={styles.emptyState}>
          <IconPlaylist size={48} stroke={1.5} opacity={0.5} />
          <Text size="lg" mt="md" fw={500}>No playlists found</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Try adjusting your search or create a new playlist
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className={styles.gridContainer}>
      <div ref={headerRef} className={`${styles.header} ${styles.gridColumns}`}>
        {PLAYLIST_GRID_COLUMNS.map((col) => {
          if (col.key === 'checkbox') {
            return (
              <div key={col.key} className={`${styles.headerCell} ${styles.cellCheckbox}`}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleHeaderCheckbox}
                  aria-label="Select all"
                />
              </div>
            );
          }

          if (col.key === 'actions') {
            return <div key={col.key} className={styles.headerCell} />;
          }

          const isActive = col.sortable && col.sortKey === sortKey;

          return (
            <div
              key={col.key}
              className={`${styles.headerCell} ${col.sortable ? styles.sortableHeader : ''}`}
              style={{
                justifyContent: col.key === 'created' || col.key === 'updated' ? 'flex-end' :
                  col.key === 'status' ? 'center' : 'flex-start',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => handleHeaderClick(col)}
            >
              {col.label}
              {col.sortable && (
                <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                  {isActive && sortDirection === 'asc' ? (
                    <IconChevronUp size={14} />
                  ) : isActive && sortDirection === 'desc' ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronDown size={14} style={{ opacity: 0.3 }} />
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        ref={parentRef}
        className={styles.body}
        style={viewportHeight > 0 ? { height: `${viewportHeight}px` } : undefined}
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {renderedVirtualRows.map((virtualRow) => {
            const playlist = getRow(virtualRow.index);
            if (!playlist) return null;

            const selected = isSelected(playlist.listId);

            return (
              <PlaylistGridRow
                key={virtualRow.key}
                playlist={playlist}
                index={virtualRow.index}
                isSelected={selected}
                onSelectOnly={selectOnly}
                onToggle={toggle}
                onRangeSelect={handleRangeSelect}
                onMenuOpen={handleMenuOpen}
                onNavigate={handleNavigate}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              />
            );
          })}
        </div>
      </div>

      <Menu
        opened={menuState.opened}
        onChange={(opened) => { if (!opened) handleMenuClose(); }}
        shadow="md"
        width={180}
        position="bottom-end"
      >
        <Menu.Target>
          <div ref={anchorRef} style={{ position: 'fixed', pointerEvents: 'none', width: 0, height: 0 }} />
        </Menu.Target>
        <Menu.Dropdown>
          {activePlaylist && (
            <>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                component={Link}
                to="/playlists/$id"
                // @ts-expect-error Mantine polymorphic component loses TanStack Router params inference
                params={{ id: activePlaylist.displayId }}
                onClick={handleMenuClose}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCode size={14} />}
                onClick={() => { handleMenuClose(); onPublish?.(activePlaylist); }}
              >
                Publish
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={activePlaylist.isActive
                  ? <IconPlayerPause size={14} />
                  : <IconPlayerPlay size={14} />}
                onClick={() => { handleMenuClose(); onActivateToggle?.(activePlaylist); }}
              >
                {activePlaylist.isActive ? 'Deactivate' : 'Activate'}
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => { handleMenuClose(); handleRowDelete(activePlaylist); }}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
